import type { Participant } from "@sgdf/ui-library";
import { sorterMap } from "~/core/utils/participant-sorter";

export const useSortParticipants = (
  participants: Ref<Participant[]> | Ref<undefined>,
) => {
  const sortStore = useSortStore();
  const route = useRoute();
  const router = useRouter();
  const meetingId = useMeetingId();

  const { sortField, sortDir } = storeToRefs(sortStore);

  onMounted(async () => {
    if (sortField && sortDir) {
      await navigateTo({
        query: {
          ...route.query,
          sortField: sortField.value,
          sortDir: sortDir.value,
        },
      });
    }
  });

  onUnmounted(() => {
    if (!Array.isArray(route.query.sortField)) {
      sortStore.setSortField(route.query.sortField ?? "first_name");
    }
    if (!Array.isArray(route.query.sortDir)) {
      sortStore.setSortDir(route.query.sortDir ?? "asc");
    }
  });

  const data = computed(() => {
    const participantsWithLink = [...(participants?.value ?? [])].map(
      (participant) => ({
        ...participant,
        link: router.resolve(
          RouteUtils.getMeetingParticipant(
            meetingId.value ?? "",
            participant.id,
            typeof route.params.view === 'string' ? route.params.view : undefined,
            route.query
          ),
        ).href,
      }),
    );

    const { sortField, sortDir } = route.query;
    if (
      Array.isArray(sortField) ||
      Array.isArray(sortDir) ||
      typeof sortField !== "string" ||
      typeof sortDir !== "string"
    ) {
      return participantsWithLink;
    }

    if (sortField === "shuffle") {
      return participantsWithLink
        // nosemgrep -- Math.random() acceptable for non-critical UI shuffling
        .map((value) => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);
    }

    const sorter =
      sorterMap[sortField ?? "first_name"]?.[
        sortDir === "asc" ? "true" : "false"
      ];

    if (sorter) {
      return participantsWithLink.sort(sorter);
    }

    return participantsWithLink;
  });

  return {
    data,
    route,
  };
};
